// deck.gl
// SPDX-License-Identifier: MIT
// Copyright (c) vis.gl contributors

import {deepEqual} from '../utils/deep-equal';
import log from '../utils/log';
import {flatten} from '../utils/flatten';

import type Controller from '../controllers/controller';
import type {ViewStateChangeParameters, InteractionState} from '../controllers/controller';
import type Viewport from '../viewports/viewport';
import type View from '../views/view';
import type {CanvasContext, PresentationContext} from '@luma.gl/core';
import type {Timeline} from '@luma.gl/engine';
import type {EventManager} from 'mjolnir.js';
import type {ConstructorOf} from '../types/types';
import type {default as MapView, MapViewState} from '../views/map-view';

export type ViewOrViews = View | View[] | null;
type ViewStateOf<ViewT> = ViewT extends View<infer ViewStateT> ? ViewStateT : never;
type OneOfViews<ViewsT extends ViewOrViews> = ViewsT extends null
  ? MapView
  : ViewsT extends View[]
    ? ViewsT[number]
    : ViewsT;
export type AnyViewStateOf<ViewsT extends ViewOrViews> = ViewStateOf<OneOfViews<ViewsT>>;
export type ViewStateMap<ViewsT extends ViewOrViews> = ViewsT extends null
  ? MapViewState
  : ViewsT extends View
    ? ViewStateOf<ViewsT>
    : {[viewId: string]: AnyViewStateOf<ViewsT>};

/** This is a very lose type of all "acceptable" viewState
 * It's not good for type hinting but matches what may exist internally
 */
export type ViewStateObject<ViewsT extends ViewOrViews> =
  | ViewStateMap<ViewsT>
  | AnyViewStateOf<ViewsT>
  | {[viewId: string]: AnyViewStateOf<ViewsT>};

/** Canvas id used by views that do not declare a presentation canvas. */
export const DEFAULT_CANVAS_ID = 'default-canvas';

/** Event manager assigned to a view's current presentation canvas. */
type ViewEventManager = {
  /** Presentation canvas receiving the view's controller events. */
  canvasId: string;
  /** Event manager for that canvas, or null when no presentation target exists. */
  eventManager: EventManager | null;
};

/**
 * Looks up an existing canvas context without duplicating its resize or pixel-size bookkeeping.
 * @param viewId - View to resolve, or undefined to select the default canvas.
 * @returns The existing render or presentation context, or null when no canvas matches.
 */
type CanvasContextResolver = (viewId?: string) => CanvasContext | PresentationContext | null;

/**
 * Restricts viewport selection to one presentation canvas, optionally at a CSS-pixel location.
 * Canvas-only filters intentionally omit coordinates so they return every matching viewport.
 */
type ViewportFilter =
  | {
      /** Presentation canvas whose viewports should be returned. */
      canvasId: string;
    }
  | {
      /** Horizontal CSS-pixel coordinate to match. */
      x: number;
      /** Vertical CSS-pixel coordinate to match. */
      y: number;
      /** Optional width of the CSS-pixel query rectangle. */
      width?: number;
      /** Optional height of the CSS-pixel query rectangle. */
      height?: number;
      /** Optional presentation canvas that further restricts the pixel query. */
      canvasId?: string;
    };

/** CSS-pixel dimensions read directly from an existing canvas context. */
type CanvasDimensions = {
  /** Canvas width in CSS pixels. */
  width: number;
  /** Canvas height in CSS pixels. */
  height: number;
};

/** Internal view, controller, and canvas-routing properties supplied by Deck. */
type ViewManagerProps<ViewsT extends ViewOrViews> = {
  views: ViewsT;
  viewState: ViewStateObject<ViewsT> | null;
  onViewStateChange?: (params: ViewStateChangeParameters<AnyViewStateOf<ViewsT>>) => void;
  onInteractionStateChange?: (state: InteractionState) => void;
  pickPosition?: (x: number, y: number, viewId?: string) => {coordinate?: number[]} | null;
  width?: number;
  height?: number;
  /** Resolve existing luma contexts, which own all canvas resize and pixel-size tracking. */
  getCanvasContext?: CanvasContextResolver;
  /** Event managers keyed by presentation canvas id. */
  eventManagers?: Record<string, EventManager>;
};

/** Builds viewports and routes controllers using their assigned canvas coordinate systems. */
export default class ViewManager<ViewsT extends View[]> {
  width: number;
  height: number;
  views: View[];
  viewState: ViewStateObject<ViewsT>;
  controllers: {[viewId: string]: Controller<any> | null};
  timeline: Timeline;

  private _viewports: Viewport[];
  private _viewportMap: {[viewId: string]: Viewport};
  private _isUpdating: boolean;
  private _needsRedraw: string | false;
  private _needsUpdate: string | false;
  private _eventManager: EventManager | null;
  private _eventManagers: Record<string, EventManager>;
  private _viewEventManagers: {[viewId: string]: ViewEventManager};
  private _eventCallbacks: {
    onViewStateChange?: (params: ViewStateChangeParameters) => void;
    onInteractionStateChange?: (state: InteractionState) => void;
  };
  private _pickPosition?: (x: number, y: number, viewId?: string) => {coordinate?: number[]} | null;
  /** Context lookup supplied by Deck; context dimensions remain owned and observed by luma. */
  private _getCanvasContext?: CanvasContextResolver;

  /**
   * Initializes the viewport layout, controller routing, and optional canvas-context lookup.
   * @param props - Initial views, view state, event managers, dimensions, and context resolver.
   */
  constructor(
    props: ViewManagerProps<ViewsT> & {
      // Initial options
      timeline: Timeline;
      eventManager: EventManager | null;
    }
  ) {
    // List of view descriptors, gets re-evaluated when width/height changes
    this.views = [];
    this.width = 100;
    this.height = 100;
    this.viewState = {} as any;
    this.controllers = {};
    this.timeline = props.timeline;

    this._viewports = []; // Generated viewports
    this._viewportMap = {};
    this._isUpdating = false;
    this._needsRedraw = 'First render';
    this._needsUpdate = 'Initialize';

    this._eventManager = props.eventManager;
    this._eventManagers = props.eventManagers || {};
    this._viewEventManagers = {};
    this._eventCallbacks = {
      onViewStateChange: props.onViewStateChange,
      onInteractionStateChange: props.onInteractionStateChange
    };
    this._pickPosition = props.pickPosition;
    this._getCanvasContext = props.getCanvasContext;

    Object.seal(this);

    // Init with default map viewport
    this.setProps(props);
  }

  /** Remove all resources and event listeners */
  finalize(): void {
    for (const key in this.controllers) {
      const controller = this.controllers[key];
      if (controller) {
        controller.finalize();
      }
    }
    this.controllers = {};
  }

  /** Check if a redraw is needed */
  needsRedraw(
    opts: {
      /** Reset redraw flags to false */
      clearRedrawFlags?: boolean;
    } = {clearRedrawFlags: false}
  ): string | false {
    const redraw = this._needsRedraw;
    if (opts.clearRedrawFlags) {
      this._needsRedraw = false;
    }
    return redraw;
  }

  /** Mark the manager as dirty. Will rebuild all viewports and update controllers. */
  setNeedsUpdate(reason: string): void {
    this._needsUpdate = this._needsUpdate || reason;
    this._needsRedraw = this._needsRedraw || reason;
  }

  /** Checks each viewport for transition updates */
  updateViewStates(): void {
    for (const viewId in this.controllers) {
      const controller = this.controllers[viewId];
      if (controller) {
        controller.updateTransition();
      }
    }
  }

  /**
   * Returns active viewports, optionally restricted by canvas and/or CSS-pixel coordinates.
   * @param filter - A canvas-only selection or a point/rectangle with an optional canvas id.
   * @returns Matching viewports in their original rendering order.
   */
  getViewports(filter?: ViewportFilter): Viewport[] {
    if (filter) {
      return this._viewports.filter(viewport => {
        // Different canvases can contain the same CSS coordinates. First select the requested
        // canvas, then test pixel containment only when coordinates were actually supplied;
        // canvas-only queries must retain every viewport on that canvas.
        const matchesCanvas = !filter.canvasId || this.getCanvasId(viewport.id) === filter.canvasId;
        const matchesPixel = !('x' in filter) || viewport.containsPixel(filter);
        return matchesCanvas && matchesPixel;
      });
    }
    return this._viewports;
  }

  /** Get a map of all views */
  getViews(): {[viewId: string]: View} {
    const viewMap = {};
    this.views.forEach(view => {
      viewMap[view.id] = view;
    });
    return viewMap;
  }

  /** Resolves a viewId string to a View */
  getView(viewId: string): View | undefined {
    return this.views.find(view => view.id === viewId);
  }

  /** Returns the viewState for a specific viewId. Matches the viewState by
    1. view.viewStateId
    2. view.id
    3. root viewState
    then applies the view's filter if any */
  getViewState(viewOrViewId: string | View): AnyViewStateOf<ViewsT> {
    const view: View | undefined =
      typeof viewOrViewId === 'string' ? this.getView(viewOrViewId) : viewOrViewId;
    // Backward compatibility: view state for single view
    const viewState = (view && this.viewState[view.getViewStateId()]) || this.viewState;
    return (view ? view.filterViewState(viewState) : viewState) as AnyViewStateOf<ViewsT>;
  }

  getViewport(viewId: string): Viewport | undefined {
    return this._viewportMap[viewId];
  }

  /**
   * Resolves the presentation canvas associated with an existing or supplied view.
   * @param viewOrViewId - View instance or id whose canvas association should be resolved.
   * @returns The assigned canvas id, or undefined when the view does not exist.
   */
  getCanvasId(viewOrViewId: string | View): string | undefined {
    const view = typeof viewOrViewId === 'string' ? this.getView(viewOrViewId) : viewOrViewId;
    return view
      ? this._viewEventManagers[view.id]?.canvasId || this._getCanvasIdFromView(view)
      : undefined;
  }

  /**
   * Unproject pixel coordinates on screen onto world coordinates,
   * (possibly [lon, lat]) on map.
   * - [x, y] => [lng, lat]
   * - [x, y, z] => [lng, lat, Z]
   * @param {Array} xyz -
   * @param {Object} opts - options
   * @param {Object} opts.topLeft=true - Whether origin is top left
   * @return {Array|null} - [lng, lat, Z] or [X, Y, Z]
   */
  unproject(xyz: number[], opts?: {topLeft?: boolean}): number[] | null {
    const viewports = this.getViewports();
    const pixel = {x: xyz[0], y: xyz[1]};
    for (let i = viewports.length - 1; i >= 0; --i) {
      const viewport = viewports[i];
      if (viewport.containsPixel(pixel)) {
        const p = xyz.slice();
        p[0] -= viewport.x;
        p[1] -= viewport.y;
        return viewport.unproject(p, opts);
      }
    }
    return null;
  }

  /** Update the manager with new Deck props */
  setProps(props: Partial<ViewManagerProps<ViewsT>>) {
    if (props.views) {
      this._setViews(props.views);
    }

    if (props.viewState) {
      this._setViewState(props.viewState);
    }

    if ('width' in props || 'height' in props) {
      this._setSize(props.width as number, props.height as number);
    }

    if ('pickPosition' in props) {
      this._pickPosition = props.pickPosition;
    }

    if ('eventManagers' in props) {
      this._setEventManagers(props.eventManagers || {});
    }

    // Important: avoid invoking _update() inside itself
    // Nested updates result in unexpected side effects inside _rebuildViewports()
    // when using auto control in pure-js
    if (!this._isUpdating) {
      this._update();
    }
  }

  //
  // PRIVATE METHODS
  //

  private _update(): void {
    this._isUpdating = true;

    try {
      // Only rebuild viewports if the update flag is set
      if (this._needsUpdate) {
        this._needsUpdate = false;
        this._rebuildViewports();
      }

      // If viewport transition(s) are triggered during viewports update, controller(s)
      // will immediately call `onViewStateChange` which calls `viewManager.setProps` again.
      if (this._needsUpdate) {
        this._needsUpdate = false;
        this._rebuildViewports();
      }
    } finally {
      // An error while rebuilding must not leave the manager unable to update ever again
      this._isUpdating = false;
    }
  }

  /**
   * Whether `getViewState(view)` resolves to a view state this view can use.
   * The root `viewState` is a valid fallback for a single view, but not when it is a map of
   * view states keyed by view id (every value a plain object, no view state fields of its own).
   */
  private _hasViewState(view: View): boolean {
    const ownViewState = view.props.viewState;
    if (ownViewState && typeof ownViewState === 'object' && !ownViewState.id) {
      // The view completely defines its own view state, see View.filterViewState
      return true;
    }
    if (this.viewState[view.getViewStateId()]) {
      return true;
    }
    const values = Object.values(this.viewState);
    return (
      values.length === 0 ||
      values.some(value => !value || typeof value !== 'object' || Array.isArray(value))
    );
  }

  private _setSize(width: number, height: number): void {
    if (width !== this.width || height !== this.height) {
      this.width = width;
      this.height = height;
      this.setNeedsUpdate('Size changed');
    }
  }

  // Update the view descriptor list and set change flag if needed
  // Does not actually rebuild the `Viewport`s until `getViewports` is called
  private _setViews(views: View[]): void {
    views = flatten(views, Boolean);

    const viewsChanged = this._diffViews(views, this.views);
    if (viewsChanged) {
      this.setNeedsUpdate('views changed');
    }

    this.views = views;
  }

  private _setViewState(viewState: ViewStateObject<ViewsT>): void {
    if (viewState) {
      // depth = 3 when comparing viewStates: viewId.position.0
      const viewStateChanged = !deepEqual(viewState, this.viewState, 3);

      if (viewStateChanged) {
        this.setNeedsUpdate('viewState changed');
      }

      this.viewState = viewState;
    } else {
      log.warn('missing `viewState` or `initialViewState`')();
    }
  }

  private _setEventManagers(eventManagers: Record<string, EventManager>): void {
    if (this._eventManagers !== eventManagers) {
      this._eventManagers = eventManagers;
      this.setNeedsUpdate('eventManagers changed');
    }
  }

  /**
   * Resolves a view's explicit canvas id, falling back to the existing default canvas context.
   * @param view - View whose presentation canvas is being determined.
   * @returns The explicit, default-context, or legacy default canvas id.
   */
  private _getCanvasIdFromView(view: View): string {
    return view.props.canvasId || this._getCanvasContext?.(view.id)?.id || DEFAULT_CANVAS_ID;
  }

  /**
   * Reads the CSS dimensions used to lay out a view and its controller viewport.
   * @param view - View whose assigned canvas provides the layout coordinate space.
   * @returns Context-owned CSS dimensions, or the manager dimensions without a canvas context.
   */
  private _getCanvasDimensions(view: View): CanvasDimensions {
    // Viewport layout uses CSS pixels, not framebuffer pixels. Read them directly from the
    // observed luma context instead of maintaining a second per-canvas size snapshot.
    const canvasContext = this._getCanvasContext?.(view.id);
    const [width, height] = canvasContext?.getCSSSize() || [this.width, this.height];
    return {width, height};
  }

  private _getViewEventManager(view: View): ViewEventManager {
    const canvasId = this.getCanvasId(view) || DEFAULT_CANVAS_ID;
    return {
      canvasId,
      eventManager: this._eventManagers[canvasId] || this._eventManager
    };
  }

  private _startViewportRebuild(): {
    oldControllers: {[viewId: string]: Controller<any> | null};
    oldViewEventManagers: {[viewId: string]: ViewEventManager};
  } {
    const oldControllers = this.controllers;
    const oldViewEventManagers = this._viewEventManagers;
    this._viewports = [];
    this.controllers = {};
    this._viewEventManagers = {};
    return {oldControllers, oldViewEventManagers};
  }

  private _getReusableController(
    controller: Controller<any> | null | undefined,
    oldViewEventManager: ViewEventManager | undefined,
    viewEventManager: ViewEventManager
  ): Controller<any> | null | undefined {
    if (
      controller &&
      (oldViewEventManager?.canvasId !== viewEventManager.canvasId ||
        oldViewEventManager?.eventManager !== viewEventManager.eventManager)
    ) {
      controller.finalize();
      return null;
    }
    return controller;
  }

  /**
   * Creates a controller that resolves its viewport against the view's assigned canvas.
   * @param view - View receiving the controller.
   * @param props - Controller identity and implementation class.
   * @returns The initialized controller for the view.
   */
  private _createController(
    view: View,
    props: {id: string; type: ConstructorOf<Controller<any>>}
  ): Controller<any> {
    const Controller = props.type;

    const controller = new Controller({
      timeline: this.timeline,
      eventManager: this._getViewEventManager(view).eventManager,
      // Set an internal callback that calls the prop callback if provided
      onViewStateChange: this._eventCallbacks.onViewStateChange,
      onStateChange: this._eventCallbacks.onInteractionStateChange,
      makeViewport: viewState =>
        this.getView(view.id)?.makeViewport({
          viewState,
          ...this._getCanvasDimensions(view)
        }),
      pickPosition: (x, y) => this._pickPosition?.(x, y, view.id)
    });

    return controller;
  }

  private _updateController(
    view: View,
    viewState: AnyViewStateOf<ViewsT>,
    viewport: Viewport | null,
    controller?: Controller<any> | null
  ): Controller<any> | null {
    const controllerProps = view.controller;
    if (controllerProps && viewport) {
      const resolvedProps = {
        ...viewState,
        ...controllerProps,
        id: view.id,
        x: viewport.x,
        y: viewport.y,
        width: viewport.width,
        height: viewport.height
      };

      // Create controller if not already existing or if the type of the
      // controller has changed.
      if (!controller || controller.constructor !== controllerProps.type) {
        controller = this._createController(view, resolvedProps);
      }
      if (controller) {
        controller.setProps(resolvedProps);
      }
      return controller;
    }
    return null;
  }

  /** Rebuilds each viewport and controller using its presentation canvas's CSS dimensions. */
  private _rebuildViewports(): void {
    const {views} = this;

    const {oldControllers, oldViewEventManagers} = this._startViewportRebuild();

    let invalidateControllers = false;
    // Create controllers in reverse order, so that views on top receive events first
    for (let i = views.length; i--; ) {
      const view = views[i];
      const {width, height} = this._getCanvasDimensions(view);
      const viewEventManager = this._getViewEventManager(view);
      this._viewEventManagers[view.id] = viewEventManager;
      const viewState = this.getViewState(view);
      const viewport = view.makeViewport({viewState, width, height});

      let oldController = this._getReusableController(
        oldControllers[view.id],
        oldViewEventManagers[view.id],
        viewEventManager
      );
      let hasController = Boolean(view.controller);
      if (hasController && !this._hasViewState(view)) {
        // View has a controller but no view state, controller disabled
        // e.g. the default view created before an application provides views, while the
        // view state is keyed by the ids of the views it will provide
        hasController = false;
      }
      if (hasController && !oldController) {
        // When a new controller is added, invalidate all controllers below it so that
        // events are registered in the correct order
        invalidateControllers = true;
      }
      if ((invalidateControllers || !hasController) && oldController) {
        // Remove and reattach invalidated controller
        oldController.finalize();
        oldController = null;
      }

      // Update the controller
      this.controllers[view.id] = hasController
        ? this._updateController(view, viewState, viewport, oldController)
        : null;

      if (viewport) {
        this._viewports.unshift(viewport);
      }
    }

    // Remove unused controllers
    for (const id in oldControllers) {
      const oldController = oldControllers[id];
      if (oldController && !this.controllers[id]) {
        oldController.finalize();
      }
    }

    this._buildViewportMap();
  }

  _buildViewportMap(): void {
    // Build a view id to view index
    this._viewportMap = {};
    this._viewports.forEach(viewport => {
      if (viewport.id) {
        // TODO - issue warning if multiple viewports use same id
        this._viewportMap[viewport.id] = this._viewportMap[viewport.id] || viewport;
      }
    });
  }

  // Check if viewport array has changed, returns true if any change
  // Note that descriptors can be the same
  _diffViews(newViews: View[], oldViews: View[]): boolean {
    if (newViews.length !== oldViews.length) {
      return true;
    }

    return newViews.some((_, i) => !newViews[i].equals(oldViews[i]));
  }
}
